import { useState } from "react";
import { Outlet, useLocation } from "react-router-dom";
import { Inbox } from "lucide-react";
import { AppShell, stratusNav } from "@/shell/AppShell";
import {
  RightRail,
  RailSection,
  AttentionList,
  ActivityFeed,
  eventToActivityItem,
  NarrativeCard,
} from "@/design/components";
import { useSeed, useOrgIndex, useMoney, useActionPanelItems } from "@/domain/hooks";
import { toAttentionItems, topAttentionSeverity } from "@/domain/actionPanel";
import { useStore } from "@/domain/store";

/** Per-destination heading. The sidebar owns navigation; no inline tab strip (spec §3.5). */
const HEADINGS: Record<string, { title: string; sub: string }> = {
  "": { title: "Oversight", sub: "Cross-programme portfolio summary for Stratus Health." },
  portfolio: { title: "Portfolio", sub: "Programmes, projects, the map and site visits: drill in from one place." },
  // TODO(heimdall): restore "Heimdall findings," in the subtitle when the AI analyst ships.
  signals: { title: "Signals", sub: "Everything flagged for review, exceptions and reconciliation, in one inbox." },
  projects: { title: "Projects", sub: "Every project across every programme you oversee, in one place." },
  approvals: { title: "Approvals", sub: "Spend asks and disbursements awaiting oversight action." },
  controls: { title: "Controls", sub: "Assurance, compliance, contractor verification and access, the accountability controls." },
  insights: { title: "Insights", sub: "Pace, burn-rate, predictive outlook and like-for-like comparison." },
  reports: { title: "Reports", sub: "Portfolio and programme reporting." },
  audit: { title: "Audit trail", sub: "Tamper-evident log of every action." },
};

function segFromPath(pathname: string): string {
  return pathname.replace(/^\/stratus\/?/, "").split("/")[0];
}

export function StratusDashboardLayout() {
  const seed = useSeed();
  const orgs = useOrgIndex();
  const money = useMoney();
  const { pathname } = useLocation();
  const [railCollapsed, setRailCollapsed] = useState(false);
  const seg = segFromPath(pathname);
  const heading = HEADINGS[seg] ?? HEADINGS[""];
  const isMap = seg === "map";
  const { title: needsYouTitle, items: actionItems } = useActionPanelItems();
  const setWorkspace = useStore((s) => s.setWorkspace);
  // A briefing item can concern a different workspace than whatever's
  // currently active — pin the right one before the row's Link navigates,
  // rather than assuming the click happened in the right context.
  const attentionItems = toAttentionItems(actionItems).map((it) => ({
    ...it,
    onClick: it.workspaceId ? () => setWorkspace(it.workspaceId!) : undefined,
  }));

  return (
    <AppShell
      context="Stratus Health"
      sections={stratusNav}
      crumbs={[{ label: "Stratus", to: "/stratus" }, { label: heading.title }]}
      rightRail={
        <RightRail
          collapsed={railCollapsed}
          onToggle={() => setRailCollapsed((c) => !c)}
          summary={[{ id: "attention", count: attentionItems.length, severity: topAttentionSeverity(attentionItems) }]}
        >
          <RailSection
            title={needsYouTitle}
            tone="action"
            icon={Inbox}
            count={attentionItems.length}
            severity={topAttentionSeverity(attentionItems)}
          >
            <AttentionList items={attentionItems} />
          </RailSection>
          <RailSection title="Briefing" divider>
            <NarrativeCard variant="briefing" title="Across the portfolio" body={seed.briefing.whereWeAre} />
          </RailSection>
          <RailSection title="Activity">
            <ActivityFeed
              density="compact"
              items={seed.auditEvents.slice(0, 6).map((e) => eventToActivityItem(e, { meta: orgs[e.actorOrganisationId]?.name, amount: e.amount ? money(e.amount) : undefined }))}
              viewAllHref="/stratus/audit"
            />
          </RailSection>
        </RightRail>
      }
    >
      <div className={isMap ? "flex h-full flex-col" : "px-4 py-5 md:px-8 md:py-6"}>
        {!isMap && (
          <div className="mb-5">
            <h1 className="text-2xl font-semibold tracking-tight text-ink">{heading.title}</h1>
            <p className="mt-1 text-sm text-mute">{heading.sub}</p>
          </div>
        )}
        <div className={isMap ? "min-h-0 flex-1" : ""}>
          <Outlet />
        </div>
      </div>
    </AppShell>
  );
}
